<template>
    <iframe
        ref="iframeRef"
        :title="`bb-widget-${bbId || bbSpaceId || 'preview'}`"
        class="bb-iframe"
        style="width: 100%; border: 0; display: block;"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    ></iframe>
</template>



<script>
    import { debounce } from 'lodash';
    import { isEmpty } from '@/utils/util';

    export default {
        name: 'IframeWidgetPreview',
        props: {
            bbType: {
                validator: value => ['widget', 'popover', 'overlay', 'logos-only'].includes(value),
            },
            bbPopoverEvent: {
                type: String,
                validator: value => ['hover', 'click'].includes(value),
            },
            bbLanguage: {
                type: String,
            },
            bbId: {
                type: Number,
            },
            bbSpaceId: {
                type: Number,
            },
            bbOid: {
                type: Number,
            },
            bbNumber: {
                type: Number,
            },
            bbName: {
                type: String,
            },
            bbInfo: {
                type: String,
            },
            bbShopStyle: {
                type: String,
            },
            bbSort: {
                type: String,
            },
            bbLeadColor: {
                type: String,
            },
            bbButtonLabel: {
                type: String,
            },
            bbRowCount: {
                type: Number,
            },
            bbShowExtra: {
                type: Boolean,
                default: undefined,
            },
            bbShowPrices: {
                type: Boolean,
                default: undefined,
            },
            bbP1: {
                type: String,
            },
            bbP2: {
                type: String,
            },
            bbP3: {
                type: String,
            },
            htmlTag: {
                type: String,
                default: 'span',
            },
            reloadDelay: {
                type: Number,
                default: 1000,
            },
            bbFormats: {
                type: String,
            },
            bbDefaultFormats: {
                type: String,
            },
        },
        data() {
            return {
                debouncedReloadInsideFrame: () => {},
                frameReady: false,
            };
        },
        computed: {
            hasTrigger() {
                return ['popover', 'overlay'].includes(this.bbType);
            },
            bbShowExtraValue() {
                if (isEmpty(this.bbShowExtra)) return undefined;
                return this.bbShowExtra ? 'product_name-info-img' : 'false';
            },
            bbShowPricesValue() {
                if (isEmpty(this.bbShowPrices)) return undefined;
                return this.bbShowPrices ? 'true' : 'false';
            },
            widgetAttributesString() {
                const attributes = {
                    id: 'bb-widget-preview',
                    class: 'bb-widget border-0 bg-none',
                    'data-bb-stats': '0',
                    'data-bb-type': this.bbType === 'widget' ? undefined : this.bbType,
                    'data-bb-popover-event': this.bbPopoverEvent,
                    'data-bb-language': this.bbLanguage,
                    'data-bb-id': this.bbId,
                    'bb-space-id': this.bbSpaceId,
                    'data-bb-oid': this.bbOid,
                    'data-bb-number': this.bbNumber,
                    'data-bb-name': this.bbName,
                    'data-bb-info': this.bbInfo,
                    'data-bb-shop-style': this.bbShopStyle,
                    'data-bb-sort': this.bbSort,
                    'data-bb-lead-color': this.bbLeadColor,
                    'data-bb-button-label': this.bbButtonLabel,
                    'data-bb-row-count': this.bbRowCount,
                    'data-bb-show-extra': this.bbShowExtraValue,
                    'data-bb-show-prices': this.bbShowPricesValue,
                    'data-bb-p1': this.bbP1,
                    'data-bb-p2': this.bbP2,
                    'data-bb-p3': this.bbP3,
                    'data-bb-alt': this.hasTrigger ? undefined : 'bb-alt',
                    'data-bb-formats': this.bbFormats,
                    'data-bb-default-formats': this.bbDefaultFormats,
                    'data-bb-version': '5.0',
                };

                return Object.entries(attributes)
                    .filter(([, value]) => value !== undefined && value !== null && value !== '')
                    .map(([key, value]) => `${key}="${String(value).replace(/"/g, '&quot;')}"`)
                    .join(' ');
            },
            altHtml() {
                if (this.hasTrigger) return '';
                return `<div id="bb-alt" class="title text-center bb-alt" style="display:none;">${this.$t('widgetPreview.noOffers')}</div>`;
            },
            triggerHtml() {
                if (!this.hasTrigger) return '';
                return `<span class="preview-link" style="text-decoration:underline;color:blue;cursor:pointer;">${this.$t('widgetPreview.show')}</span>`;
            },
        },
        watch: {
            // Każda zmiana propów — przebudowanie srcdoc i reload w ramce
            $props: {
                deep: true,
                handler() {
                    this.refreshIframe();
                },
            },
        },
        methods: {
            onFrameMessage(event) {
                // Filtrowanie po naszym originie nie zawsze możliwe przy allow-same-origin=false,
                // ale mamy sandbox z same-origin, więc event.origin == window.location.origin
                if (!event || !event.data || typeof event.data !== 'object') return;

                const { kind, payload } = event.data;

                if (kind === 'bb:height') {
                    const iframeEl = this.$refs.iframeRef;
                    if (iframeEl) iframeEl.style.height = `${payload?.height || 0}px`;
                }
                if (kind === 'bb:event') {
                    // Propagacja eventów z ramki do rodzica, zgodnie z API
                    const { name, detail } = payload || {};
                    if (name === 'bb-loading') this.$emit('BBLoading', detail);
                    if (name === 'bb-no-data') this.$emit('NoData', detail);
                    if (name === 'bb-loaded') this.$emit('BBLoaded', detail);
                }
                if (kind === 'bb:ready') {
                    this.frameReady = true;
                    // Po gotowości poprośmy o forceReload (gdy mamy już window.bb)
                    this.debouncedReloadInsideFrame();
                }
            },
            postReloadToFrame() {
                const iframeWindow = this.$refs.iframeRef?.contentWindow;
                if (iframeWindow) {
                    iframeWindow.postMessage({ kind: 'bb:forceReload' }, '*');
                    this.$emit('reloadBB'); // informacyjnie dla rodzica
                }
            },
            buildSrcdocHtml() {
                const widgetScriptUrl = process.env.VUE_APP_WIDGET_SCRIPT_URL;

                // UWAGA: rozbijamy tagi <script>, aby parser SFC nie zamknął bloku <script> pliku .vue
                const openScript = '<scr' + 'ipt>'; // eslint-disable-line
                const closeScript = '</scr' + 'ipt>'; // eslint-disable-line

                return `
                    <!doctype html>
                    <html>
                        <head>
                        <meta charset="utf-8" />
                        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
                        <style>
                            html, body { margin:0; padding:0; }
                            .bb-container { display:block; width:100%; }
                        </style>
                        </head>
                        <body>
                            <div class="bb-container">
                                <${this.htmlTag} ${this.widgetAttributesString}>
                                ${this.triggerHtml}
                                </${this.htmlTag}>
                                ${this.altHtml}
                            </div>

                            ${openScript}
                                (function(){
                                const resizeObserver = new ResizeObserver(function(){
                                    const h = document.documentElement.scrollHeight || document.body.scrollHeight;
                                    parent.postMessage({ kind: 'bb:height', payload: { height: h } }, '*');
                                });
                                resizeObserver.observe(document.documentElement);

                                // nasłuchujemy eventów na elemencie, dokumencie i oknie (jak w oryginale Vue)
                                const widgetEl = document.getElementById('bb-widget-preview');
                                ['bb-loading', 'bb-no-data', 'bb-loaded'].forEach(eventName => {
                                    const handler = e => {
                                        parent.postMessage({
                                            kind: 'bb:event',
                                            payload: {
                                                name: eventName,
                                                detail: { type: e?.type || null, timeStamp: e?.timeStamp || null }
                                            }
                                        }, '*');

                                        if (eventName === 'bb-loaded') {
                                            const h = document.documentElement.scrollHeight || document.body.scrollHeight;
                                            parent.postMessage({ kind: 'bb:height', payload: { height: h } }, '*');
                                        }
                                    };

                                    if (widgetEl) widgetEl.addEventListener(eventName, handler, { capture: true });
                                    document.addEventListener(eventName, handler, { capture: true });
                                    window.addEventListener(eventName, handler, { capture: true });
                                });

                                window.addEventListener('message', function(ev){
                                    if (ev?.data?.kind === 'bb:forceReload' && window.bb && typeof window.bb.forceReload === 'function') {
                                    window.bb.forceReload();
                                    }
                                });

                                var s = document.createElement('script');
                                s.src = ${JSON.stringify(widgetScriptUrl)};
                                s.async = true;
                                s.onload = function(){
                                    parent.postMessage({ kind: 'bb:ready' }, '*');
                                };
                                document.body.appendChild(s);
                                })();
                            ${closeScript}
                        </body>
                    </html>
                    `.trim();
            },
            refreshIframe() {
                const iframeEl = this.$refs.iframeRef;
                if (!iframeEl) return;

                // Ustawiamy srcdoc za każdym razem, gdy propy się zmieniają
                const html = this.buildSrcdocHtml();
                // Ważne: wyczyszczenie wysokości, aby uniknąć artefaktów podczas przebudowy
                iframeEl.style.height = '0px';
                iframeEl.srcdoc = html;
                this.frameReady = false;
            },
        },
        mounted() {
            this.debouncedReloadInsideFrame = debounce(this.postReloadToFrame, this.reloadDelay);
            window.addEventListener('message', this.onFrameMessage);
            this.refreshIframe();
        },
        beforeDestroy() {
            window.removeEventListener('message', this.onFrameMessage);
        },
    };
</script>



<style lang="scss" scoped>
    .preview-link {
        text-decoration: underline;
        color: blue;
        cursor: pointer;
    }
</style>
